/**
 * CampaignCard
 *
 * Card universal de campaña que renderiza variantes según el `origin` y el
 * `status`. Reemplaza al ObjectiveCard viejo (que solo conocía draft/active/
 * paused).
 *
 * Variantes principales:
 *
 *   ── Automática (origin === 'automatic') ──
 *     Badge indigo "⚡ Automática" + chip "Always-on 🔒" si isAlwaysOn.
 *     CTA: "Ver dashboard". Solo pausa/reanuda; sin delete si es always-on.
 *
 *   ── Sugerida (origin === 'suggested') ──
 *     status === 'suggested': Badge violeta + glow + 3 CTAs (Ver detalle).
 *     status === 'dismissed': greyed out + opción Reactivar.
 *     status === 'active' (después de aceptar): igual que automatic.
 *
 *   ── Mía (origin === 'custom') ──
 *     status === 'draft': "Configurar y enviar a revisión".
 *     status === 'pending_review': spinner + "Revisando..." pulse.
 *     status === 'needs_changes': "Revisar observaciones" + chip iteración.
 *     status === 'active' / 'paused': igual que las otras pero con review trail.
 */

import { Link } from "react-router-dom";
import {
  Play,
  Pause,
  Pencil,
  Copy,
  Trash2,
  MoreVertical,
  Loader2,
  Users,
  Target,
  BarChart3,
  ArrowRight,
  Settings2,
  Lock,
  Sparkles,
  AlertTriangle,
  Eye,
  Send,
  X as XIcon,
  RefreshCw,
  Mail,
  MessageCircle,
  Bell,
  Zap,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";
import type { Objective } from "../types";
import { PRESET_BY_KEY } from "../types";
import { STATUS_META, ORIGIN_META } from "../api";

interface CampaignCardProps {
  campaign: Objective;
  busy?: boolean;
  onActivate: () => void;
  onPause: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  /** Click sobre el área principal (NO en CTAs ni kebab) — abre el modal correspondiente */
  onClick?: () => void;
  /** Abrir el Trigger Studio para editar canales + contenido + triggers */
  onOpenStudio?: () => void;
}

/**
 * Cuando el status pill diría algo redundante con el origin badge.
 * Por ejemplo: origin "Sugerida" + status "Sugerida por Deenex" → omitir
 * el status pill, el origin alcanza.
 */
function isStatusRedundant(
  origin: "automatic" | "suggested" | "custom",
  status: string,
): boolean {
  if (origin === "suggested" && status === "suggested") return true;
  return false;
}

export default function CampaignCard({
  campaign,
  busy = false,
  onActivate,
  onPause,
  onDuplicate,
  onDelete,
  onClick,
  onOpenStudio,
}: CampaignCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const preset = PRESET_BY_KEY[campaign.preset];
  const m = campaign.metrics;
  const origin = campaign.origin;
  const status = campaign.status;
  const originMeta = ORIGIN_META[origin];
  const statusMeta = STATUS_META[status];

  const isDraft = status === "draft";
  const isActive = status === "active";
  const isPaused = status === "paused";
  const isSuggested = status === "suggested";
  const isPending = status === "pending_review";
  const isNeedsChanges = status === "needs_changes";
  const isDismissed = status === "dismissed";

  // Dispatcher de click: si está en estado que abre modal, prevenir Link y disparar onClick
  const opensModal = isSuggested || isPending || isNeedsChanges;

  const lastRun = m.lastRunAt
    ? new Date(m.lastRunAt).toLocaleDateString("es-AR", {
        day: "numeric",
        month: "short",
      })
    : null;

  // Destino del Link si la card no abre modal
  const detailHref = isDraft
    ? `/objectives/${campaign.id}/setup`
    : `/objectives/${campaign.id}`;

  // Glow/ring por origen
  const glowClass =
    origin === "suggested" && isSuggested
      ? "ring-2 ring-violet-200/60 shadow-lg shadow-violet-100/60"
      : isPending
        ? "ring-2 ring-blue-200/60"
        : isNeedsChanges
          ? "ring-2 ring-amber-200/60"
          : "";

  return (
    <div
      className={`group relative bg-white rounded-2xl border shadow-sm transition-all duration-300 overflow-hidden ${
        busy
          ? "opacity-60 pointer-events-none border-slate-100"
          : "border-slate-100 hover:border-slate-200 hover:shadow-md hover:-translate-y-0.5"
      } ${isDismissed ? "saturate-50" : ""} ${glowClass}`}
      style={{ background: preset?.bgGradient }}
    >
      {/* Header tape: badge de origen + chip de always-on
          El status pill se omite cuando dice lo mismo que el origin badge
          (e.g. origin "Sugerida" + status "Sugerida por Deenex" = redundante).
          NO usamos uppercase porque queda inconsistente con los chips de
          filtro (ej. "Mías" vs "MIA"). */}
      <div className="relative px-5 pt-4 pb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border flex items-center gap-1 ${originMeta.classes}`}
          >
            <span>{originMeta.emoji}</span>
            {originMeta.label}
          </span>
          {campaign.isAlwaysOn && (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 flex items-center gap-1">
              <Lock size={9} />
              Always-on
            </span>
          )}
          {isNeedsChanges && campaign.aiReview && (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
              Revisión #{campaign.aiReview.iteration}
            </span>
          )}
        </div>
        {/* Status pill — solo si añade info nueva sobre el origin badge */}
        {!isStatusRedundant(origin, status) && (
          <span
            className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border flex items-center gap-1 ${statusMeta.classes}`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${statusMeta.dotClass}`}
            />
            {statusMeta.label}
          </span>
        )}
      </div>

      {/* Clickable area: link OR onClick */}
      {opensModal ? (
        <button
          onClick={onClick}
          className="block w-full text-left px-5 pb-3 cursor-pointer"
        >
          <CardBody campaign={campaign} preset={preset} />
        </button>
      ) : (
        <Link to={detailHref} className="block px-5 pb-3">
          <CardBody campaign={campaign} preset={preset} />
        </Link>
      )}

      {/* Action bar — varía por estado */}
      <div className="flex items-center justify-between gap-2 px-5 pb-4 relative z-10">
        {isSuggested ? (
          <button
            onClick={onClick}
            className="group/btn flex-1 py-2.5 px-3 rounded-xl text-sm font-semibold bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:from-violet-700 hover:to-fuchsia-700 active:scale-[0.98] text-center transition-all duration-200 flex items-center justify-center gap-2 shadow-sm shadow-violet-500/20"
          >
            <Sparkles size={15} />
            Revisar sugerencia
            <ArrowRight
              size={14}
              className="transition-transform group-hover/btn:translate-x-0.5"
            />
          </button>
        ) : isPending ? (
          <button
            onClick={onClick}
            className="flex-1 py-2.5 px-3 rounded-xl text-sm font-semibold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 text-center transition-all flex items-center justify-center gap-2"
          >
            <Loader2 size={14} className="animate-spin" />
            La IA está revisando…
          </button>
        ) : isNeedsChanges ? (
          <button
            onClick={onClick}
            className="group/btn flex-1 py-2.5 px-3 rounded-xl text-sm font-semibold bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200 active:scale-[0.98] text-center transition-all duration-200 flex items-center justify-center gap-2"
          >
            <Eye size={15} />
            Ver observaciones ({campaign.aiReview?.observations.length || 0})
            <ArrowRight
              size={14}
              className="transition-transform group-hover/btn:translate-x-0.5"
            />
          </button>
        ) : isDraft ? (
          <Link
            to={`/objectives/${campaign.id}/setup`}
            className="group/btn flex-1 py-2.5 px-3 rounded-xl text-sm font-semibold bg-violet-600 text-white hover:bg-violet-700 active:scale-[0.98] text-center transition-all duration-200 flex items-center justify-center gap-2 shadow-sm shadow-violet-500/20"
          >
            <Settings2 size={15} />
            Configurar y enviar
            <ArrowRight
              size={14}
              className="transition-transform group-hover/btn:translate-x-0.5"
            />
          </Link>
        ) : isDismissed ? (
          <button
            onClick={onClick}
            className="flex-1 py-2.5 px-3 rounded-xl text-sm font-semibold bg-white text-violet-700 border border-violet-200 hover:bg-violet-50 hover:border-violet-300 transition-all flex items-center justify-center gap-2 shadow-sm"
          >
            <RefreshCw size={14} />
            Revisar de nuevo
          </button>
        ) : (
          <Link
            to={`/objectives/${campaign.id}`}
            className="group/btn flex-1 py-2.5 px-3 rounded-xl text-sm font-semibold bg-violet-600 text-white hover:bg-violet-700 active:scale-[0.98] text-center transition-all duration-200 flex items-center justify-center gap-2 shadow-sm shadow-violet-500/20"
          >
            <BarChart3 size={15} />
            Ver dashboard
            <ArrowRight
              size={14}
              className="transition-transform group-hover/btn:translate-x-0.5"
            />
          </Link>
        )}

        {/* Kebab — solo si hay acciones contextuales */}
        {!isSuggested && !isPending && !isDismissed && (
          <div className="relative" ref={menuRef}>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMenuOpen(!menuOpen);
              }}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-white/80 transition-colors border border-slate-200/60 bg-white/60"
              aria-label="Más acciones"
            >
              <MoreVertical size={16} />
            </button>

            {menuOpen && (
              <div className="absolute right-0 bottom-full mb-1 w-48 bg-white rounded-xl border border-slate-200 shadow-lg z-20 overflow-hidden animate-fade-in">
                {isActive && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setMenuOpen(false);
                      onPause();
                    }}
                    className="w-full px-3 py-2 text-left flex items-center gap-2 text-xs text-amber-700 hover:bg-amber-50 transition"
                  >
                    <Pause size={13} />
                    Pausar envíos
                  </button>
                )}
                {isPaused && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setMenuOpen(false);
                      onActivate();
                    }}
                    className="w-full px-3 py-2 text-left flex items-center gap-2 text-xs text-emerald-700 hover:bg-emerald-50 transition"
                  >
                    <Play size={13} />
                    Reanudar envíos
                  </button>
                )}
                {(isActive || isPaused) && (
                  <div className="border-t border-slate-100" />
                )}

                {/* NUEVO: Studio de canales + triggers */}
                {onOpenStudio && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setMenuOpen(false);
                      onOpenStudio();
                    }}
                    className="w-full px-3 py-2 text-left flex items-center gap-2 text-xs text-violet-700 hover:bg-violet-50 transition font-semibold"
                  >
                    <Zap size={13} />
                    Canales y triggers
                  </button>
                )}

                {/* Editar copy: siempre disponible, también para always-on */}
                <Link
                  to={`/objectives/${campaign.id}/setup`}
                  onClick={() => setMenuOpen(false)}
                  className="w-full px-3 py-2 text-left flex items-center gap-2 text-xs text-slate-700 hover:bg-slate-50 transition"
                >
                  <Pencil size={13} />
                  Editar copy y segmento
                </Link>

                {/* Duplicar y borrar: NO disponibles para always-on (su lógica
                    es única — no tiene sentido duplicar un carrito abandonado) */}
                {!campaign.isAlwaysOn && (
                  <>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setMenuOpen(false);
                        onDuplicate();
                      }}
                      className="w-full px-3 py-2 text-left flex items-center gap-2 text-xs text-slate-700 hover:bg-slate-50 transition"
                    >
                      <Copy size={13} />
                      Duplicar
                    </button>
                    <div className="border-t border-slate-100" />
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setMenuOpen(false);
                        onDelete();
                      }}
                      className="w-full px-3 py-2 text-left flex items-center gap-2 text-xs text-red-600 hover:bg-red-50 transition"
                    >
                      <Trash2 size={13} />
                      Borrar
                    </button>
                  </>
                )}
                {campaign.isAlwaysOn && (
                  <div className="px-3 py-2.5 text-[11px] text-slate-500 leading-relaxed border-t border-slate-100">
                    <Lock size={11} className="inline mr-1 -mt-0.5" />
                    Las always-on no se pueden duplicar ni borrar — solo
                    pausar o editar el copy.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Dismissed reason — overlay sutil al pie si dismissed */}
      {isDismissed && campaign.dismissReason && (
        <div className="px-5 pb-3 -mt-1">
          <p className="text-[10px] text-slate-400 italic flex items-center gap-1.5">
            <XIcon size={9} />
            Descartaste: "{campaign.dismissReason}"
          </p>
        </div>
      )}
    </div>
  );
}

/* ─── Card body sub-component ─── */

function CardBody({
  campaign,
  preset,
}: {
  campaign: Objective;
  preset: any;
}) {
  const m = campaign.metrics;
  const status = campaign.status;
  const isSuggested = status === "suggested";
  const isPending = status === "pending_review";
  const isNeedsChanges = status === "needs_changes";
  const isDraft = status === "draft";
  const showMetrics =
    !isSuggested &&
    !isPending &&
    !isNeedsChanges &&
    !isDraft &&
    status !== "dismissed";

  const lastRun = m.lastRunAt
    ? new Date(m.lastRunAt).toLocaleDateString("es-AR", {
        day: "numeric",
        month: "short",
      })
    : "Nunca";

  return (
    <>
      {/* Title row */}
      <div className="flex items-start gap-3 mb-3">
        <div className="text-3xl flex-shrink-0 leading-none">
          {campaign.emoji}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-bold text-slate-900 leading-tight">
            {campaign.name}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2 leading-snug">
            {preset?.shortDescription}
          </p>
        </div>
      </div>

      {/* Suggestion preview */}
      {isSuggested && campaign.suggestionReason && (
        <div className="bg-violet-50/80 border border-violet-100 rounded-xl p-3 mb-1">
          <p className="text-[10px] font-bold uppercase tracking-wider text-violet-600 mb-1 flex items-center gap-1">
            <Sparkles size={10} />
            Por qué te la sugiero
          </p>
          <p className="text-xs text-slate-700 leading-relaxed line-clamp-3">
            {campaign.suggestionReason}
          </p>
        </div>
      )}

      {/* Needs changes summary */}
      {isNeedsChanges && campaign.aiReview && (
        <div className="bg-amber-50/80 border border-amber-100 rounded-xl p-3 mb-1">
          <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1 flex items-center gap-1">
            <AlertTriangle size={10} />
            La IA observó {campaign.aiReview.observations.length} cosa
            {campaign.aiReview.observations.length === 1 ? "" : "s"}
          </p>
          <p className="text-xs text-slate-700 leading-snug line-clamp-2">
            {campaign.aiReview.observations[0]?.message}
          </p>
        </div>
      )}

      {/* Pending review — pulse loader */}
      {isPending && (
        <div className="bg-blue-50/80 border border-blue-100 rounded-xl p-3 mb-1 flex items-center gap-2.5">
          <Loader2 size={14} className="animate-spin text-blue-600" />
          <p className="text-xs text-blue-700 font-medium">
            La IA está chequeando tono, frecuencia y conflictos…
          </p>
        </div>
      )}

      {/* Draft hint */}
      {isDraft && (
        <div className="bg-white/70 rounded-xl p-3 mb-1">
          <p className="text-xs text-slate-500 leading-relaxed">
            ⚙️{" "}
            <span className="font-medium text-slate-700">Falta completar</span>{" "}
            — luego enviá a revisión.{" "}
            <Link
              to={`/objectives/${campaign.id}/setup`}
              className="text-violet-600 font-semibold hover:underline"
            >
              Completar ahora →
            </Link>
          </p>
        </div>
      )}

      {/* Metrics — solo cuando está corriendo */}
      {showMetrics && (
        <div className="grid grid-cols-3 gap-2 bg-white/70 rounded-xl p-3 mb-1">
          <Metric
            icon={Users}
            label="Alcance"
            value={m.peopleReached.toLocaleString("es-AR")}
          />
          <Metric
            icon={Target}
            label="Conversiones"
            value={m.conversions.toLocaleString("es-AR")}
          />
          <Metric
            icon={Send}
            label="Último envío"
            value={lastRun}
          />
        </div>
      )}

      {/* Chip row: canales + triggers (solo si tiene alguno seteado) */}
      {(campaign.channels?.length || campaign.triggers?.items.length) && (
        <div className="flex items-center gap-1.5 flex-wrap mt-2">
          {campaign.channels?.includes("email") && (
            <ChannelChip icon={<Mail size={10} />} label="Email" color="blue" />
          )}
          {campaign.channels?.includes("whatsapp") && (
            <ChannelChip
              icon={<MessageCircle size={10} />}
              label="WhatsApp"
              color="emerald"
            />
          )}
          {campaign.channels?.includes("push") && (
            <ChannelChip
              icon={<Bell size={10} />}
              label="Push"
              color="violet"
            />
          )}
          {campaign.triggers && campaign.triggers.items.length > 0 && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 flex items-center gap-1 ml-auto">
              <Zap size={9} />
              {campaign.triggers.items.length} trigger
              {campaign.triggers.items.length === 1 ? "" : "s"} ·{" "}
              {campaign.triggers.logic === "AND" ? "TODOS" : "CUALQUIERA"}
            </span>
          )}
        </div>
      )}
    </>
  );
}

function ChannelChip({
  icon,
  label,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  color: "blue" | "emerald" | "violet";
}) {
  const styles: Record<string, string> = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
  };
  return (
    <span
      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border flex items-center gap-1 ${styles[color]}`}
    >
      {icon}
      {label}
    </span>
  );
}

/* ─── Sub: metric cell ─── */

interface MetricProps {
  icon?: typeof Users;
  label: string;
  value: string;
}

function Metric({ icon: Icon, label, value }: MetricProps) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">
        {Icon && <Icon size={10} />}
        <span className="truncate">{label}</span>
      </div>
      <p className="text-sm font-bold text-slate-900 truncate">{value}</p>
    </div>
  );
}
